import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, TextInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { FileText, Plus, Search, Trash2, Copy } from "lucide-react";
import { fmtINR, fmtDate, fmtPct } from "@/lib/format";
import { toast } from "sonner";

const STATUS_TONE = {
    DRAFT: { tone: "slate", label: "Draft" },
    PENDING_DH: { tone: "amber", label: "Pending DH" },
    PENDING_DIRECTOR: { tone: "copper", label: "Pending Director" },
    APPROVED: { tone: "emerald", label: "Approved" },
    DIRECTOR_APPROVED: { tone: "emerald", label: "Director Approved" },
    HELD: { tone: "red", label: "Held" },
};

export default function RFQList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [filterCust, setFilterCust] = useState("ALL");
    const [filterStatus, setFilterStatus] = useState("ALL");
    const canCreate = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const canDelete = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        Promise.all([api.get("/rfqs"), api.get("/customers")])
            .then(([r, c]) => { setRows(r.data); setCustomers(c.data); })
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!rows) return null;
        return rows.filter((r) => {
            if (search && !(r.reference?.toLowerCase().includes(search.toLowerCase()))) return false;
            if (filterCust !== "ALL" && r.customer_id !== filterCust) return false;
            if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
            return true;
        });
    }, [rows, search, filterCust, filterStatus]);

    const overdue = (r) => r.response_due_date && new Date(r.response_due_date) < new Date() && !["APPROVED", "DIRECTOR_APPROVED"].includes(r.status);
    const urgent = (r) => {
        if (!r.response_due_date) return false;
        const ms = new Date(r.response_due_date).getTime() - Date.now();
        return ms > 0 && ms < 24 * 60 * 60 * 1000;
    };

    const duplicate = async (r) => {
        try {
            const { data } = await api.post(`/rfqs/${r.id}/duplicate`);
            toast.success(`Duplicated as ${data.reference}`);
            navigate(`/rfq/${data.id}/edit`);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Delete ${r.reference}?`)) return;
        try { await api.delete(`/rfqs/${r.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="rfq-list-page">
            <PageHeader
                eyebrow="MODULE · STEP 3 OF 4"
                title="RFQs · Commercial Offers"
                subtitle="Customer price quotations. Commercial norms auto-load when you pick a customer; never typed."
                actions={canCreate && (
                    <CopperButton onClick={() => navigate("/rfq/new")} data-testid="rfq-new-btn">
                        <Plus className="h-4 w-4" /> New RFQ
                    </CopperButton>
                )}
            />

            <Card className="p-3 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="relative col-span-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by RFQ reference…" className="pl-8 h-8 text-[12px]" />
                    </div>
                    <Select value={filterCust} onChange={(e) => setFilterCust(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All customers</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </Select>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All statuses</option>
                        {Object.entries(STATUS_TONE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </Select>
                </div>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {filtered && filtered.length === 0 && (
                <EmptyState
                    icon={FileText}
                    title="No RFQs yet"
                    description={canCreate ? "Create your first commercial offer from a ZBC." : "Awaiting Finance to create the first RFQ."}
                    action={canCreate && <CopperButton onClick={() => navigate("/rfq/new")}><Plus className="h-4 w-4" /> Create First RFQ</CopperButton>}
                />
            )}

            {filtered && filtered.length > 0 && (
                <Card>
                    <table className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-3">Reference</th>
                                <th className="eyebrow text-left py-2 px-3">Customer</th>
                                <th className="eyebrow text-right py-2 px-3">Parts</th>
                                <th className="eyebrow text-right py-2 px-3">Final Price</th>
                                <th className="eyebrow text-right py-2 px-3">Margin %</th>
                                <th className="eyebrow text-left py-2 px-3">Status</th>
                                <th className="eyebrow text-left py-2 px-3">Created</th>
                                <th className="eyebrow text-left py-2 px-3">Due</th>
                                <th className="eyebrow text-right py-2 px-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const st = STATUS_TONE[r.status] || { tone: "slate", label: r.status };
                                const p = r.pricing || {};
                                return (
                                    <tr key={r.id} className="border-b border-slate-100">
                                        <td className="py-1.5 px-3 font-mono font-semibold text-[13px]">
                                            <Link to={`/rfq/${r.id}`} className="hover:text-[hsl(var(--copper))]">{r.reference}</Link>
                                        </td>
                                        <td className="py-1.5 px-3 text-[13px]">{r.customer_code}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{(r.parts || []).length}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px] font-semibold">{fmtINR(p.final_price || 0)}</td>
                                        <td className={`py-1.5 px-3 text-right font-mono tabular-nums text-[13px] ${p.margin_pct >= 15 ? "text-emerald-600" : p.margin_pct >= 8 ? "text-amber-600" : "text-red-600"}`}>
                                            {fmtPct(p.margin_pct || 0)}
                                        </td>
                                        <td className="py-1.5 px-3"><Pill tone={st.tone}>{st.label}</Pill></td>
                                        <td className="py-1.5 px-3 text-[11px] font-mono text-slate-500">{fmtDate(r.created_at)}<br/><span className="text-slate-400">{r.created_by_name}</span></td>
                                        <td className={`py-1.5 px-3 text-[11px] font-mono ${overdue(r) ? "text-red-600 font-semibold" : urgent(r) ? "text-amber-600 font-semibold" : "text-slate-500"}`}>
                                            {fmtDate(r.response_due_date)}
                                            {overdue(r) && <div className="text-[10px] uppercase tracking-wider">Overdue</div>}
                                            {urgent(r) && !overdue(r) && <div className="text-[10px] uppercase tracking-wider">&lt;24h</div>}
                                        </td>
                                        <td className="py-1.5 px-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => navigate(`/rfq/${r.id}`)} variant="ghost" className="h-7 px-2 text-[11px]">View</SlateButton>
                                                {canCreate && ["DRAFT", "HELD"].includes(r.status) && (
                                                    <SlateButton onClick={() => navigate(`/rfq/${r.id}/edit`)} variant="ghost" className="h-7 px-2 text-[11px]">Edit</SlateButton>
                                                )}
                                                {canCreate && <SlateButton onClick={() => duplicate(r)} variant="ghost" className="h-7 w-7 px-0" title="Duplicate"><Copy className="h-3 w-3" /></SlateButton>}
                                                {canDelete && <SlateButton onClick={() => remove(r)} variant="ghost" className="h-7 w-7 px-0 text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="h-3 w-3" /></SlateButton>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}
